import { css } from "@linaria/core";
import cn from "classnames";
import React from "react";
import type { Options } from "@ui/Highcharts";
import Highchart from "@ui/Highcharts";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import Card from "@ui/Highcharts/ui/Card";
import Legend from "@ui/Highcharts/ui/Legend";
import ReportingChartSubtitle from "./Subtitle";
import type { ReportingChartTitleProps } from "./Title";
import ReportingChartTitle from "./Title";
import type { PointOptionsObject, SeriesOptionsType } from "highcharts";
import EmptyChart from "./EmptyChart";
import {
  FullViewportButtonMarker,
  FullViewportButtonMarkerActive,
  FullViewportButtonMarkerInactive,
} from "@ui/FullViewportButton";
import type { Reporting } from "../../../../api/useReporting";
import { exportWidgetTarget } from "@ui/ExportWidgetButton";

export type ReportingChartDefinition = ReportingChartTitleProps & {
  id: string;
};

export type ReportingChartProps = {
  chartRef?: React.RefObject<Highcharts.Chart>;
  id: ReportingChartDefinition["id"];
  reporting: Reporting;
  title: ReportingChartDefinition["title"];
  description: ReportingChartDefinition["description"];
  matrixSelector?: React.ReactNode;
  dateSelector?: React.ReactNode;
  leftOptions?: React.ReactNode;
  rightOptions?: React.ReactNode;
  normalizeNavigatorSeries?: boolean;
  options: Options;
  series: Highcharts.SeriesOptionsType[];
  extraSubtitleInfo?: string;
  selectablePoints?: boolean;
  className?: string;
};

const hidden = css`
  display: none;
`;

const ChartContainer = css`
  grid-template-columns: 0.15fr 1fr;
  grid-template-rows: 45vh;

  .${FullViewportButtonMarkerActive} &,
  .${FullViewportButtonMarkerInactive} & {
    height: 100%;
    grid-template-rows: 1fr;
  }

  :not(.${hidden}) {
    display: grid;
  }
`;

/**
 * ReportingChart component
 * @description
 * ReportingChart component is a wrapper around Highcharts component and allow reussable chart only define by its definition, series and options
 *
 * @param props ReportingChart component props
 * @param props.definition ReportingChart definition, as chart share exact same layout
 * @param props.series ReportingChart series
 * @param props.options ReportingChart options
 * @param props.leftOptions ReportingChart left options to pass filter component, top left side of the chart
 * @param props.rightOptions ReportingChart right options to pass filter component, top right side of the chart
 * @param props.normalizeNavigatorSeries ReportingChart normalizeNavigatorSeries
 * @returns
 */
function ReportingChart({
  id,
  reporting,
  title,
  matrixSelector,
  dateSelector,
  description,
  series,
  options,
  leftOptions,
  rightOptions,
  normalizeNavigatorSeries,
  chartRef,
  selectablePoints = false,
  extraSubtitleInfo,
  className,
}: React.PropsWithChildren<ReportingChartProps>) {
  const chart = React.useRef<Highcharts.Chart>(null);
  const [visibleSeries, setVisibleSeries] = useVisibleSeries(id, series);

  const hasSerieData = React.useMemo(
    () =>
      (series as (SeriesOptionsType & { data: PointOptionsObject[] })[]).some(
        ({ data }) =>
          data?.length > 0 && (data as PointOptionsObject[]).some(({ y }) => y !== null),
      ),
    [series],
  );

  const deferredVisibleSeries = React.useDeferredValue(visibleSeries);
  const deferredOptions = React.useDeferredValue(options);

  return (
    <Card
      title={
        <ReportingChartTitle
          title={title}
          matrixSelector={matrixSelector}
          dateSelector={dateSelector}
          description={description}
          exportTitle={title}
        />
      }
      subtitle={<ReportingChartSubtitle extraInfo={extraSubtitleInfo} reporting={reporting} />}
      leftOptions={leftOptions}
      rightOptions={rightOptions}
      className={cn(className, FullViewportButtonMarker, exportWidgetTarget)}
    >
      <div className={cn(ChartContainer, { [hidden]: !hasSerieData })}>
        <Legend
          chartRefs={[chartRef || chart]}
          series={deferredVisibleSeries}
          onLegendItemClick={setVisibleSeries}
        />
        <Highchart
          ref={chartRef || chart}
          options={deferredOptions}
          name={id}
          selectablePoints={selectablePoints}
          normalizeNavigatorSeries={normalizeNavigatorSeries}
        />
      </div>
      <EmptyChart className={cn({ [hidden]: hasSerieData })} />
    </Card>
  );
}

export default React.memo(ReportingChart);
